import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { MarkEmailReadOutlined } from '@mui/icons-material';
import { emailService } from '../../services/emailService';
import { useAutoFetch } from '../../hooks/useAutoFetch';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import useTimezone from '../../hooks/useTimezone';
import type { EmailLogEntry, EmailStatus, EmailTypeSetting } from '../../types/email';
import StatusChip, { statusLabel } from '../common/StatusChip';
import SearchableSelect from '../common/SearchableSelect';
import TableRowSkeletons from '../common/TableRowSkeletons';
import EmptyState from '../common/EmptyState';
import ResendEmailButton from './ResendEmailButton';
import { useRefreshWhileSending } from './useRefreshWhileSending';

const STATUSES: EmailStatus[] = ['sent', 'failed', 'sending'];
const PAGE_SIZES = [10, 25, 50];
const COLUMNS: Array<{ label: string; width?: number | string }> = [
  { label: 'Email' },
  { label: 'To', width: '27%' },
  { label: 'Status', width: 104 },
  { label: 'When', width: 160 },
  { label: '', width: 124 },
];

const ActivityRow: React.FC<{ log: EmailLogEntry; canResend: boolean; onResent: () => void }> = React.memo(({ log, canResend, onResent }) => {
  const { formatDateTime } = useTimezone();
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" fontWeight={600} noWrap>{log.typeLabel}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div" title={log.subject}>{log.subject}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" noWrap>{log.entityLabel}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div" title={log.to.join(', ')}>{log.to.join(', ')}</Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={log.status === 'failed' ? log.error ?? '' : ''} placement="top">
          <span><StatusChip status={{ kind: 'email', value: log.status }} size="small" /></span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" noWrap>{formatDateTime(log.sentAt ?? log.createdAt, 'dd MMM, hh:mm a')}</Typography>
        {log.sentBy && <Typography variant="caption" color="text.secondary" noWrap component="div">{log.sentBy.name}</Typography>}
      </TableCell>
      <TableCell align="right">{canResend && <ResendEmailButton log={log} onResent={onResent} />}</TableCell>
    </TableRow>
  );
});

/** Every email the school sent, newest first, with failures one click from Resend (any email can be resent from its record). */
const EmailActivity: React.FC<{ types: EmailTypeSetting[] }> = ({ types }) => {
  const theme = useTheme();
  const { canWrite } = useModulePermissions();
  const [status, setStatus] = useState<EmailStatus | ''>('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZES[0]);

  const { data, loading, retry } = useAutoFetch(
    JSON.stringify({ status, type, page, rowsPerPage }),
    () => emailService.log({ status: status || undefined, type: type || undefined, page: page + 1, limit: rowsPerPage }),
  );
  const refresh = useCallback(() => retry(), [retry]);
  useRefreshWhileSending(data?.items, refresh);

  const typeOptions = useMemo(() => types.map(t => ({ value: t.key, label: t.label, secondary: t.group })), [types]);
  const moduleOf = useMemo(() => new Map(types.map(t => [t.key, t.module])), [types]);
  const total = data ? STATUSES.reduce((n, s) => n + data.counts[s], 0) : 0;
  const items = data?.items ?? [];
  const filtered = !!(status || type);

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
          <ToggleButtonGroup
            size="small" exclusive value={status}
            onChange={(_, v) => { if (v !== null) { setStatus(v); setPage(0); } }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontWeight: 500, whiteSpace: 'nowrap' } }}
          >
            {[{ value: '' as const, label: 'All', n: total }, ...STATUSES.map(s => ({ value: s, label: statusLabel({ kind: 'email', value: s }), n: data?.counts[s] ?? 0 }))].map(o => (
              <ToggleButton key={o.value} value={o.value}>
                {o.label}
                {data && <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{o.n}</Box>}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <SearchableSelect label="Email" value={type} onChange={v => { setType(v); setPage(0); }} options={typeOptions} placeholder="All emails" minWidth={220} />
      </Stack>
      {loading && items.length > 0 && <LinearProgress sx={{ height: 2 }} />}
      <TableContainer>
        <Table sx={{ tableLayout: 'fixed', minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025) }}>
              {COLUMNS.map((c, i) => (
                <TableCell key={i} sx={{ width: c.width, fontWeight: 600, color: 'text.secondary' }}>{c.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!data ? (
              <TableRowSkeletons columns={COLUMNS.length} rows={4} />
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={{ py: 5 }}>
                  <EmptyState
                    dense
                    icon={<MarkEmailReadOutlined />}
                    title={filtered ? 'No emails match' : 'No emails sent yet'}
                    description={filtered ? 'Try another status or email.' : 'Emails switched on in Setup appear here as they go out.'}
                    action={filtered ? { label: 'Show all', onClick: () => { setStatus(''); setType(''); setPage(0); } } : undefined}
                  />
                </TableCell>
              </TableRow>
            ) : items.map(log => (
              <ActivityRow key={log._id} log={log} canResend={log.status === 'failed' && canWrite(moduleOf.get(log.type) ?? '')} onResent={refresh} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {data && data.pagination.totalItems > 0 && (
        <TablePagination
          component="div"
          count={data.pagination.totalItems}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={PAGE_SIZES}
        />
      )}
    </Paper>
  );
};

export default EmailActivity;
